import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../utils/axiosInstance';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import StatusBadge from '../components/StatusBadge';
import { localeLabel } from '../utils/localeFlag';

export default function ProductDetail() {
  const { id } = useParams();
  const [meta, setMeta] = useState(null);
  const [content, setContent] = useState(null);
  const [locales, setLocales] = useState([]);
  const [locale, setLocale] = useState('');
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});       // { "resourceId::key": "edited value" }
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    axios.get('api/transly/locales/').then((res) => {
      const nonPrimary = res.data.filter((l) => !l.primary);
      setLocales(nonPrimary);
      if (nonPrimary.length > 0) setLocale(nonPrimary[0].locale);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get('api/transly/products/', { params: { productId: id } })
      .then((res) => setMeta(res.data.products?.[0]))
      .catch(() => {});
  }, [id]);

  const fetchContent = useCallback(async () => {
    if (!locale) return;
    setLoading(true);
    try {
      const [contentRes, historyRes] = await Promise.all([
        axios.get('api/transly/product-content/', { params: { productId: id, locale } }),
        axios.get('api/transly/history/', { params: { productId: id, locale, limit: 50 } }),
      ]);
      setContent(contentRes.data);
      setHistory(historyRes.data.entries || []);
    } catch (err) {
      console.error('Failed to fetch content:', err);
    } finally {
      setLoading(false);
    }
  }, [id, locale]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get('api/transly/jobs/', { params: { id: jobId } });
        setJobStatus(res.data.status);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(interval);
          setTranslating(false);
          setJobId(null);
          fetchContent();
        }
      } catch { clearInterval(interval); }
    }, 2000);
    return () => clearInterval(interval);
  }, [jobId, fetchContent]);

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      const res = await axios.post('api/transly/translate/', { productIds: [id], targetLocale: locale });
      setJobId(res.data.jobId);
      setJobStatus('in_progress');
    } catch (err) {
      alert('Translation failed: ' + (err.response?.data?.error || err.message));
      setTranslating(false);
    }
  };

  const setEdit = (resourceId, key, value) => {
    setEdits(prev => ({ ...prev, [`${resourceId}::${key}`]: value }));
  };

  const getEdit = (resourceId, key) => edits[`${resourceId}::${key}`];
  const hasEdits = Object.keys(edits).length > 0;

  const handleCancelEdit = () => {
    setEditing(false);
    setEdits({});
  };

  const handlePushEdits = async () => {
    setPushing(true);
    try {
      // Group edits by resourceId
      const byResource = {};
      for (const [compositeKey, value] of Object.entries(edits)) {
        const [resourceId, fieldKey] = compositeKey.split('::');
        if (!byResource[resourceId]) byResource[resourceId] = [];
        // Find the digest for this field
        let digest = null;
        let sourceValue = null;
        if (resourceId === content.resourceId) {
          const field = content.fields?.find(f => f.key === fieldKey);
          digest = field?.digest;
          sourceValue = field?.value;
        } else {
          const nested = content.nestedResources?.find(n => n.resourceId === resourceId);
          const field = nested?.translatableContent?.find(f => f.key === fieldKey);
          digest = field?.digest;
          sourceValue = field?.value;
        }
        if (digest) {
          byResource[resourceId].push({ key: fieldKey, value, digest, sourceValue });
        }
      }

      // Push each resource's translations
      const errors = [];
      for (const [resourceId, translations] of Object.entries(byResource)) {
        try {
          await axios.post('api/transly/push-translation/', { resourceId, locale, translations });
        } catch (err) {
          errors.push(`${resourceId}: ${err.response?.data?.error || err.message}`);
        }
      }

      if (errors.length > 0) {
        alert('Some translations failed:\n' + errors.join('\n'));
      }

      setEditing(false);
      setEdits({});
      fetchContent();
    } catch (err) {
      alert('Push failed: ' + err.message);
    } finally {
      setPushing(false);
    }
  };

  const mainFields = content?.fields?.filter(
    (f) => ['title', 'body_html', 'handle', 'meta_title', 'meta_description', 'product_type'].includes(f.key)
  ) || [];

  const getTranslation = (key) => content?.translations?.find((t) => t.key === key);
  const getHistoryEntry = (key) => history.find((h) => h.field_key === key);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">{meta?.p_title || `Product ${id}`}</h1>
        <p className="text-sm text-muted-foreground">{meta?.p_vendor} - {meta?.p_product_type}</p>
      </div>

      {/* Locale selector + translate button */}
      <div className="flex gap-2 mb-6">
        {locales.map((l) => (
          <Button key={l.locale} variant={locale === l.locale ? 'default' : 'outline'} size="sm" onClick={() => setLocale(l.locale)}>
            {localeLabel(l.locale)}
          </Button>
        ))}
        <Button variant="outline" className="ml-auto" asChild>
          <a href={`https://admin.shopify.com/store/lamptwist-live/products/${id}`} target="_blank" rel="noopener noreferrer">
            Open in Shopify
          </a>
        </Button>
        {!editing ? (
          <>
            <Button variant="outline" onClick={() => setEditing(true)} disabled={translating || loading}>
              Edit Translations
            </Button>
            <Button onClick={handleTranslate} disabled={translating}>
              {translating ? `Translating... (${jobStatus})` : `Translate to ${localeLabel(locale)}`}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={handleCancelEdit} disabled={pushing}>
              Cancel
            </Button>
            <Button onClick={handlePushEdits} disabled={pushing || !hasEdits}>
              {pushing ? 'Pushing...' : `Push ${Object.keys(edits).length} change${Object.keys(edits).length !== 1 ? 's' : ''}`}
            </Button>
          </>
        )}
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading content...</div>
      ) : (
        <div className="space-y-4">
          {mainFields.map((field) => {
            const translation = getTranslation(field.key);
            const histEntry = getHistoryEntry(field.key);
            return (
              <Card key={field.key}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-medium">{field.key}</CardTitle>
                    <div className="flex gap-2 items-center">
                      {translation?.outdated && <StatusBadge status="outdated" />}
                      {histEntry?.seo_approved !== null && (
                        <StatusBadge status={histEntry?.seo_approved ? 'translated' : 'partial'} />
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">English (source)</p>
                      <div className="text-sm bg-muted p-2 rounded-md min-h-[40px] break-words text-foreground">
                        {field.value || <span className="text-muted-foreground italic">empty</span>}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{localeLabel(locale)}</p>
                      {editing ? (
                        field.key === 'body_html' ? (
                          <textarea
                            className="w-full text-sm bg-background border border-input p-2 rounded-md min-h-[80px] break-words text-foreground resize-y"
                            value={getEdit(content.resourceId, field.key) ?? translation?.value ?? ''}
                            onChange={(e) => setEdit(content.resourceId, field.key, e.target.value)}
                            rows={4}
                          />
                        ) : (
                          <input
                            type="text"
                            className="w-full text-sm bg-background border border-input p-2 rounded-md min-h-[40px] text-foreground"
                            value={getEdit(content.resourceId, field.key) ?? translation?.value ?? ''}
                            onChange={(e) => setEdit(content.resourceId, field.key, e.target.value)}
                          />
                        )
                      ) : (
                        <div className="text-sm bg-primary/5 p-2 rounded-md min-h-[40px] break-words text-foreground">
                          {translation?.value || <span className="text-muted-foreground italic">not translated</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Nested resources — grouped by type, option values nested under options */}
          {content?.nestedResources?.length > 0 && (() => {
            const options = content.nestedResources.filter(n => n.resourceId.includes('/ProductOption/'));
            const optionValues = content.nestedResources.filter(n => n.resourceId.includes('/ProductOptionValue/'));
            const metafields = content.nestedResources.filter(n => n.resourceId.includes('/Metafield/'));
            const images = content.nestedResources.filter(n => n.resourceId.includes('/MediaImage/'));
            const other = content.nestedResources.filter(n =>
              !n.resourceId.includes('/ProductOption/') &&
              !n.resourceId.includes('/ProductOptionValue/') &&
              !n.resourceId.includes('/Metafield/') &&
              !n.resourceId.includes('/MediaImage/')
            );

            // Group option values by their parent option name (displayName = "Color: Black" → parent = "Color")
            const valuesByOption = {};
            for (const val of optionValues) {
              const parentName = val.displayName?.split(':')?.[0]?.trim() || 'Unknown';
              if (!valuesByOption[parentName]) valuesByOption[parentName] = [];
              valuesByOption[parentName].push(val);
            }

            const renderItem = (nested, colorClass) => {
              const label = nested.displayName
                || nested.translatableContent?.[0]?.value
                || nested.resourceId.split('/').pop();
              return (
                <Card key={nested.resourceId} className={`border ${colorClass}`}>
                  <CardContent className="pt-3 pb-3">
                    <p className="text-xs font-semibold text-foreground mb-2">{label}</p>
                    {nested.translatableContent
                      ?.filter((f) => f.value)
                      .map((f) => {
                        const trans = nested.translations?.find((t) => t.key === f.key);
                        return (
                          <div key={f.key} className="grid grid-cols-2 gap-2 mb-1">
                            <div className="text-sm"><span className="text-muted-foreground">{f.key}:</span> <span className="text-foreground">{f.value}</span></div>
                            <div className="text-sm text-primary">
                              {editing ? (
                                <input
                                  type="text"
                                  className="w-full text-sm bg-background border border-input px-2 py-1 rounded text-foreground"
                                  value={getEdit(nested.resourceId, f.key) ?? trans?.value ?? ''}
                                  onChange={(e) => setEdit(nested.resourceId, f.key, e.target.value)}
                                />
                              ) : (
                                trans?.value || <span className="text-muted-foreground italic">not translated</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </CardContent>
                </Card>
              );
            };

            return (
              <>
                {/* Product Options with their values nested underneath */}
                {options.length > 0 && (
                  <div className="mt-8">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">Product Options</span>
                      <span className="text-xs text-muted-foreground">{options.length} option{options.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-4">
                      {options.map((option) => {
                        const optionName = option.translatableContent?.[0]?.value || option.resourceId.split('/').pop();
                        const childValues = valuesByOption[optionName] || [];
                        return (
                          <div key={option.resourceId}>
                            {renderItem(option, 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800')}
                            {childValues.length > 0 && (
                              <div className="ml-6 mt-1 space-y-1 border-l-2 border-purple-200 dark:border-purple-800 pl-3">
                                <span className="text-xs text-muted-foreground">{childValues.length} value{childValues.length !== 1 ? 's' : ''}</span>
                                {childValues.map((val) => renderItem(val, 'bg-purple-50 border-purple-200 dark:bg-purple-950 dark:border-purple-800'))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Metafields */}
                {metafields.length > 0 && (
                  <div className="mt-8">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">Metafields</span>
                      <span className="text-xs text-muted-foreground">{metafields.length} item{metafields.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-2">
                      {metafields.map((n) => renderItem(n, 'bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-800'))}
                    </div>
                  </div>
                )}

                {/* Image Alt Text */}
                {images.length > 0 && (
                  <div className="mt-8">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">Image Alt Text</span>
                      <span className="text-xs text-muted-foreground">{images.length} item{images.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-2">
                      {images.map((n) => renderItem(n, 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'))}
                    </div>
                  </div>
                )}

                {/* Other */}
                {other.length > 0 && (
                  <div className="mt-8">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">Other</span>
                      <span className="text-xs text-muted-foreground">{other.length} item{other.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="space-y-2">
                      {other.map((n) => renderItem(n, 'bg-gray-50 border-gray-200 dark:bg-gray-900 dark:border-gray-700'))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          {/* Translation History */}
          {history.length > 0 && (
            <div className="mt-8">
              <h2 className="font-semibold text-lg text-foreground mb-4">Translation History</h2>
              <Card>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    {history.map((entry, idx) => (
                      <div key={entry.id || idx} className="border-b border-border pb-3 last:border-0 last:pb-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-foreground">{entry.field_key}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(entry.created_at).toLocaleString()}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                            entry.model_used === 'manual'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                          }`}>
                            {entry.model_used === 'manual' ? 'Manual' : 'AI'}
                          </span>
                          {entry.seo_approved !== null && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                              entry.seo_approved
                                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                            }`}>
                              {entry.seo_approved ? 'SEO OK' : 'SEO Corrected'}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-sm text-muted-foreground break-words">
                            {entry.source_value || <span className="italic">no source</span>}
                          </div>
                          <div className="text-sm text-foreground break-words">
                            {entry.translated_value}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
